import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ChevronDown, Download, Briefcase, Mail } from "lucide-react";

const Hero = () => {
  const parallaxRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (parallaxRef.current) {
        const scrolled = window.pageYOffset;
        const parallax = parallaxRef.current;
        const speed = 0.5;
        parallax.style.transform = `translateY(${scrolled * speed}px)`;
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToAbout = () => {
    const aboutSection = document.querySelector("#about");
    if (aboutSection) {
      aboutSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section id="hero" className="hero">
      <div ref={parallaxRef} className="hero-background">
        <div className="cosmic-stars"></div>
      </div>
      <div className="hero-particles"></div>
      <div className="hero-geometry">
        <div className="floating-shape"></div>
        <div className="floating-shape"></div>
        <div className="floating-shape"></div>
        <div className="floating-shape"></div>
      </div>
      <div className="container">
        <motion.div
          className="hero-content"
          initial={{ opacity: 0, z: -100 }}
          animate={{ opacity: 1, z: 0 }}
          transition={{ duration: 1, ease: [0.215, 0.61, 0.355, 1] }}
        >
          <motion.h1
            className="hero-title"
            initial={{ y: 100, opacity: 0, rotateX: -90 }}
            animate={{ y: 0, opacity: 1, rotateX: 0 }}
            transition={{
              delay: 0.4,
              duration: 0.8,
              ease: [0.175, 0.885, 0.32, 1.275],
            }}
            whileHover={{
              scale: 1.02,
              transition: { duration: 0.3 },
            }}
          >
            Hi, I'm Rishvanth 👋
          </motion.h1>

          <motion.p
            className="hero-subtitle"
            initial={{ y: 100, opacity: 0, rotateX: -45 }}
            animate={{ y: 0, opacity: 1, rotateX: 0 }}
            transition={{
              delay: 0.6,
              duration: 0.8,
              ease: [0.175, 0.885, 0.32, 1.275],
            }}
            whileHover={{
              scale: 1.01,
              y: -3,
              transition: { duration: 0.3 },
            }}
          >
            Full-Stack Developer & DevOps Engineer
          </motion.p>

          <motion.p
            className="hero-description"
            initial={{ y: 100, opacity: 0, rotateX: -30 }}
            animate={{ y: 0, opacity: 1, rotateX: 0 }}
            transition={{
              delay: 0.7,
              duration: 0.8,
              ease: [0.175, 0.885, 0.32, 1.275],
            }}
            whileHover={{
              scale: 1.01,
              y: -2,
              transition: { duration: 0.3 },
            }}
          >
            Building scalable, secure, and high-performance applications with
            Go, React, and Docker.
          </motion.p>

          {/* GitHub Contribution Heatmap */}
          <motion.div
            className="github-heatmap"
            initial={{ y: 100, opacity: 0, scale: 0.9 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            transition={{
              delay: 0.75,
              duration: 0.8,
              ease: [0.175, 0.885, 0.32, 1.275],
            }}
            whileHover={{
              scale: 1.02,
              y: -5,
              transition: { duration: 0.3 },
            }}
          >
            <img
              src="https://ghchart.rshah.org/7D53F6/rishvanth03"
              alt="Rishvanth's GitHub Contribution Chart"
              className="github-chart"
            />
          </motion.div>
        </motion.div>
      </div>

      <motion.div
        className="scroll-indicator"
        initial={{ opacity: 0, y: 50, rotateX: -90 }}
        animate={{
          opacity: 1,
          y: [0, -10, 0],
          rotateX: 0,
          transition: {
            opacity: {
              delay: 1.5,
              duration: 0.8,
              ease: [0.175, 0.885, 0.32, 1.275],
            },
            rotateX: {
              delay: 1.5,
              duration: 0.8,
              ease: [0.175, 0.885, 0.32, 1.275],
            },
            y: {
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 2.5,
            },
          },
        }}
        whileHover={{
          scale: 1.2,
          y: -10,
          rotateY: 180,
          transition: { duration: 0.4 },
        }}
        onClick={scrollToAbout}
      >
        <ChevronDown size={32} color="white" />
      </motion.div>
    </section>
  );
};

export default Hero;
